import React, { useEffect, useRef } from 'react';

const BARS = 92;

export default function TalkRing({ mode = 'idle', energy = 0 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return undefined;

    const level = new Float32Array(BARS);
    const seed = Array.from({ length: BARS }, () => Math.random() * Math.PI * 2);
    let phase = Math.random() * 99;
    let current = 0.12;
    let raf = 0;
    let size = { w: 0, h: 0 };
    let cancelled = false;

    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      size = { w: r.width, h: r.height };
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(canvas);
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const draw = () => {
      if (cancelled) return;
      const { w, h } = size;
      const target = mode === 'listening' ? Math.max(0.45, energy)
        : mode === 'thinking' ? 0.28
        : mode === 'speaking' ? Math.max(0.4, energy)
        : 0.12;
      current += (target - current) * 0.07;
      if (w) {
        ctx.clearRect(0, 0, w, h);
        const cx = w / 2;
        const cy = h / 2;
        const S = Math.min(w, h);
        const r0 = S * 0.34;
        const maxLen = S * 0.13;
        phase += mode === 'idle' ? 0.02 : 0.05;
        for (let i = 0; i < BARS; i += 1) {
          const s = Math.sin(phase * 1.5 + seed[i]) * Math.sin(phase * 0.61 + seed[i] * 1.7);
          const t = 0.08 + Math.abs(s) * 0.85 * current + (Math.sin(phase * 0.5 - i * 0.14) + 1) / 2 * 0.08;
          level[i] += (t - level[i]) * (t > level[i] ? 0.38 : 0.12);
          const v = Math.min(level[i], 1.25);
          const a = (i / BARS) * Math.PI * 2 - Math.PI / 2;
          const len = maxLen * v;
          const hue = 132 - (i / BARS) * 108;
          ctx.strokeStyle = `hsl(${hue} ${46 + Math.min(v, 1) * 16}% ${24 + Math.min(v, 1) * 20}%)`;
          ctx.lineWidth = Math.max(2, S * 0.009);
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
          ctx.lineTo(cx + Math.cos(a) * (r0 + len), cy + Math.sin(a) * (r0 + len));
          ctx.stroke();
        }
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, [mode, energy]);

  return <canvas ref={canvasRef} className="talk-ring-canvas" aria-hidden="true" />;
}
